import { Component, OnInit, ChangeDetectionStrategy, inject } from '@angular/core';
import { IonApp, IonRouterOutlet } from '@ionic/angular';
import { RouterModule } from '@angular/router';
import { StatusBar, Style } from '@capacitor/status-bar';
import { Platform, NavController } from '@ionic/angular';
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { Router } from '@angular/router';
import { environment } from '../environments/environment';
import { PwaPromptComponent } from './core/pwa/components/pwa-prompt/pwa-prompt.component';
import { PwaService } from './core/pwa/services/pwa.service';
import { PwaTriggerService } from './core/pwa/services/pwa-trigger.service';
import { PwaDemoService } from './core/pwa/services/pwa-demo.service';

@Component({
  selector: 'app-root',
  template: `
    <ion-app>
      <ion-router-outlet></ion-router-outlet>
      <app-pwa-prompt></app-pwa-prompt>
    </ion-app>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IonApp, IonRouterOutlet, RouterModule, PwaPromptComponent]
})
export class AppComponent implements OnInit {
  private platform = inject(Platform);
  private navCtrl = inject(NavController);
  private router = inject(Router);
  private pwaService = inject(PwaService);
  private pwaTriggerService = inject(PwaTriggerService);
  private pwaDemoService = inject(PwaDemoService);

  constructor() { 
    console.log('AppComponent: Constructor called');
    console.log('AppComponent: PWA Services injected:', {
      pwaService: !!this.pwaService,
      pwaTriggerService: !!this.pwaTriggerService,
      pwaDemoService: !!this.pwaDemoService
    });
  }

  ngOnInit() {
    this.initializeApp();
  }

  async initializeApp() {
    await this.platform.ready();

    if (Capacitor.isPluginAvailable('StatusBar') && Capacitor.getPlatform() === 'android') {
      // Configurar el StatusBar para Android
      StatusBar.setOverlaysWebView({ overlay: false });
      StatusBar.setBackgroundColor({ color: '#0d3017' });
      StatusBar.setStyle({ style: Style.Light });
    }

    // Manejar el botón de retroceso en Android.
    // Se usa subscribeWithPriority(>0) para reemplazar el handler por defecto
    // que NavController registra con prioridad 0; si se usara App.addListener
    // junto a él, ambos dispararían un retroceso y la app volvería dos páginas.
    if (Capacitor.getPlatform() === 'android') {
      this.platform.backButton.subscribeWithPriority(10, async () => {
        // Rutas principales donde queremos minimizar la app al presionar atrás
        const mainRoutes = ['', '/', '/menu'];

        // En páginas principales —o cuando el stack no tiene nada que
        // desapilar— minimizar la app; en las demás, navegar hacia atrás.
        if (mainRoutes.includes(this.router.url) || !(await this.navCtrl.pop())) {
          App.minimizeApp();
        }
      });
    }
    
    // Inicializar serviços PWA
    this.initializePwaServices();
    
    // Habilitar modo debug PWA em desenvolvimento
    this.enablePwaDebugMode();
  }
  
  private initializePwaServices(): void {
    console.log('AppComponent: Initializing PWA services...');
    
    try {
      // Inicializar o serviço de trigger PWA
      console.log('AppComponent: Calling pwaTriggerService.initialize()');
      this.pwaTriggerService.initialize();
      console.log('AppComponent: PWA services initialized successfully');
    } catch (error) {
      console.error('AppComponent: Error initializing PWA services:', error);
    }
  }
  
  private enablePwaDebugMode(): void {
    // Apenas em desenvolvimento
    if (!environment.production) {
      this.pwaDemoService.enableDebugMode();
    }
  }
}
